import { z } from "zod";
import { portfolioContextListingSchema } from "./portfolio.listings";
import { min } from "date-fns";
import { MAX_IMAGE_SIZE as APP_MAX_IMAGE_SIZE } from "@/constants/images";
import { HOUSE_FEATURE_KEYS, ROOM_FEATURE_KEYS } from "@/generated/contracts";

export const MAX_IMAGE_SIZE = APP_MAX_IMAGE_SIZE;
export const MAX_IMAGES = 10;

export enum ListingPublishStatus {
  DRAFT = "draft",
  ACTIVE = "active",
  INACTIVE = "inactive",
}

export enum Gender {
  MALE = "male",
  FEMALE = "female",
  ANY = "any",
}

export enum ListingType {
  DEMAND = "demand",
  SUPPLY = "supply",
}

export const getAddNewRoomListingSchema = (t: (key: string) => string) => {
  const schema = z.object({
    title: z.string().min(5, t("titleMinLength")),
    rentPrice: z
      .string()
      .min(1, t("rentPriceRequired"))
      .regex(/^\d+(\.\d{1,2})?$/, t("invalidNumber")),
    deposit: z
      .string()
      .min(1, t("depositRequired"))
      .regex(/^\d+(\.\d{1,2})?$/, t("invalidNumber"))
      .optional(),
    description: z.string().optional(),
    latitude: z.string().optional(),
    longitude: z.string().optional(),
    locationNormalizedName: z.string().optional(),
    parishName: z.string().optional(),
    municipalityName: z.string().optional(),
    districtName: z.string().optional(),
    locationCodes: z.array(z.string()).optional(),
    propertyType: z.string().min(1, t("propertyTypeRequired")),
    businessType: z.string().min(1, t("businessTypeRequired")),
    bedrooms: z.number().optional(),
    bathrooms: z.number().optional(),
    availableFrom: z.preprocess(
      (val) => {
        if (val === null || val === undefined || val === "") return undefined;
        if (val instanceof Date) return val;
        const parsed = new Date(String(val));
        return isNaN(parsed.getTime()) ? undefined : parsed;
      },
      z.date().refine(
        (d) => {
          if (!d) return true;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return d.getTime() >= today.getTime();
        },
        { message: t("dateMustBeFuture") },
      ),
    ),
    availableTo: z.preprocess(
      (val) => {
        if (val === null || val === undefined || val === "") return undefined;
        if (val instanceof Date) return val;
        const parsed = new Date(String(val));
        return isNaN(parsed.getTime()) ? undefined : parsed;
      },
      z
        .date()
        .optional()
        .refine(
          (d) => {
            if (!d) return true;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return d.getTime() >= today.getTime();
          },
          { message: t("dateMustBeFuture") },
        ),
    ),
    // File = a new upload; string = an existing image URL kept from a source
    // listing/property (duplicate / prefill). Existing URLs are shared by
    // reference on the BE, not re-uploaded.
    images: z
      .array(z.union([z.instanceof(File), z.string()]))
      .refine(
        (items) =>
          !items ||
          items.every(
            (it) => it instanceof File === false || it.size <= MAX_IMAGE_SIZE,
          ),
        { message: t("imageMaxSize") },
      ),
    gender: z.nativeEnum(Gender).optional(),
    maxPersons: z.number().optional(),
    petFriendly: z.boolean().optional(),
    contract: z.boolean().optional(),
    smokingAllowed: z.boolean().optional(),
    roomFeatures: z.array(z.enum(ROOM_FEATURE_KEYS)).optional(),
    houseFeatures: z.array(z.enum(HOUSE_FEATURE_KEYS)).optional(),
    matchAlertsEnabled: z.boolean().optional(),
    listingType: z.nativeEnum(ListingType),
    associatedUnitId: z.string().optional(),
    associatedUnitTitle: z.string().optional(),
    associatedPortfolioTitle: z.string().optional(),
    associatedPropertyImages: z.array(z.string()).optional(),
  });

  return schema.superRefine((data, ctx) => {
    const { availableFrom, availableTo } = data as {
      availableFrom?: Date;
      availableTo?: Date;
    };
    if (availableFrom && availableTo) {
      if (availableTo.getTime() <= availableFrom.getTime()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("availableToAfterFrom"),
          path: ["availableTo"],
        });
      }
    }

    if (data.listingType === ListingType.DEMAND) {
      if (!data.locationCodes || data.locationCodes.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("locationCodesRequired"),
          path: ["locationCodes"],
        });
      }
    } else {
      if (!data.latitude || data.latitude.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("latitudeRequired"),
          path: ["latitude"],
        });
      }
      if (!data.longitude || data.longitude.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("longitudeRequired"),
          path: ["longitude"],
        });
      }
    }
  });
};

export type AddNewRoomListingSchemaType = z.infer<
  ReturnType<typeof getAddNewRoomListingSchema>
>;

export const createRoomResponseSchema = z.object({
  id: z.string(),
  slug: z.string().optional(),
});

export type CreateRoomResponseType = z.infer<typeof createRoomResponseSchema>;

export const listingSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  reference: z.string(),
  propertyUrl: z.string().nullable().optional(),
  title: z.string(),
  slug: z.string(),
  agency: z.string().optional(),
  description: z.string(),
  rentPrice: z.string(),
  condition: z.string().nullable(),
  roomArea: z.string().nullable(),
  rentPriceLastChange: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  availableFrom: z.string().nullable(),
  availableTo: z.string().nullable(),
  pictures: z.array(z.string().url()),
  thumbnails: z.array(z.string()).nullable().optional(),
  smalls: z.array(z.string()).nullable().optional(),
  businessType: z.string(),
  propertyType: z.string(),
  isPublished: z.nativeEnum(ListingPublishStatus),
  matchAlertsEnabled: z.boolean().optional(),
  userImage: z.string().url().nullable(),
  latitude: z.string().nullable().optional(),
  longitude: z.string().nullable().optional(),
  locationNormalizedName: z.string().nullable().optional(),
  parishName: z.string().nullable().optional(),
  municipalityName: z.string().nullable().optional(),
  districtName: z.string().nullable().optional(),
  bedrooms: z.union([z.number(), z.string()]).optional().nullable(),
  bathrooms: z.union([z.number(), z.string()]).optional().nullable(),
  listingType: z.nativeEnum(ListingType).optional(),
  uniqueViewCount: z.number().optional(),
  viewCount: z.number().optional(),
  favoritesCount: z.number().optional(),
  lastViewedAt: z.string().nullable().optional(),
  associatedUnit: z
    .object({
      id: z.string().uuid(),
      title: z.string(),
      unitType: z.number(),
      propertyBusinessId: z.string().uuid(),
      propertyTitle: z.string().nullable().optional(),
    })
    .optional(),
  associatedProperty: z
    .object({
      bedrooms: z.union([z.number(), z.string()]).nullable().optional(),
      bathrooms: z.union([z.number(), z.string()]).nullable().optional(),
      houseFeatures: z.array(z.string()).optional(),
      locationNormalizedName: z.string().nullable().optional(),
      latitude: z.union([z.number(), z.string()]).nullable().optional(),
      longitude: z.union([z.number(), z.string()]).nullable().optional(),
      petFriendly: z.boolean().nullable().optional(),
      contract: z.boolean().nullable().optional(),
      smokingAllowed: z.boolean().nullable().optional(),
    })
    .optional(),
});

export const demandLocationSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  normalizedName: z.string().nullable().optional(),
  administrativeLevel: z.string().nullable().optional(),
  code: z.string().nullable().optional(),
});
export type DemandLocation = z.infer<typeof demandLocationSchema>;

export const roomListingSchema = listingSchema.extend({
  maxPersons: z.union([z.number(), z.string()]).optional(),
  gender: z.nativeEnum(Gender).optional(),
  deposit: z.string().nullable().optional(),
  petFriendly: z.boolean().optional(),
  contract: z.boolean().optional(),
  smokingAllowed: z.boolean().optional(),
  roomFeatures: z.array(z.string()).optional(),
  houseFeatures: z.array(z.string()).optional(),
  portfolioContext: portfolioContextListingSchema.optional(),
  demandLocations: z.array(demandLocationSchema).optional(),
  propertyImages: z.array(z.string()).optional(),
  propertyThumbnails: z.array(z.string()).optional(),
});
export type ListingSchemaType = z.infer<typeof roomListingSchema>;

/**
 * Listing match — opposite-type listing surfaced on the owner's personal
 * listing page (e.g. supplies see matching demands and vice-versa).
 */
export const listingMatchSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  slug: z.string(),
  thumbnails: z.array(z.string()).nullable(),
  pictures: z.array(z.string()).nullable(),
  salePrice: z.string().nullable(),
  rentPrice: z.string().nullable(),
  rentCurrency: z.string().nullable(),
  saleCurrency: z.string().nullable(),
  businessType: z.string().nullable(),
  listingType: z.string().nullable(),
  realEstateType: z.string().nullable(),
  bedrooms: z.number().nullable(),
  bathrooms: z.number().nullable(),
  totalArea: z.string().nullable(),
  createdAt: z.string().nullable(),
  /**
   * Resolved display image for the row. Supply listings → first listing
   * picture; demand listings → owner's photo (since demands have no property
   * pictures, the owner's avatar IS the listing image).
   */
  image: z.string().nullable().optional(),
  ownerName: z.string().nullable().optional(),
  ownerId: z.string().uuid().nullable().optional(),
  /** Parish name (preferred) of the matching listing. */
  locationName: z.string().nullable().optional(),
});
export type ListingMatch = z.infer<typeof listingMatchSchema>;

export const listingMatchesSchema = z.object({
  matches: z.array(listingMatchSchema),
  total: z.number(),
});
export type ListingMatches = z.infer<typeof listingMatchesSchema>;

/** Create-response payload: the matches for the new listing + how many owners are being alerted. */
export const matchAlertsResultSchema = listingMatchesSchema.extend({
  notifiedCount: z.number(),
});
export type MatchAlertsResult = z.infer<typeof matchAlertsResultSchema>;

/** A listing row shown in the alerts bell "your listings with alerts" section. */
export const alertListingSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  slug: z.string(),
  thumbnails: z.array(z.string()).nullable(),
  pictures: z.array(z.string()).nullable(),
  businessType: z.string().nullable(),
  listingType: z.string().nullable(),
  matchAlertsEnabled: z.boolean(),
});
export type AlertListing = z.infer<typeof alertListingSchema>;

export const roomListingDetailSchema = roomListingSchema
  .pick({
    title: true,
    rentPrice: true,
    description: true,
    maxPersons: true,
    gender: true,
    deposit: true,
    roomFeatures: true,
  })
  .extend({
    availableFrom: z.union([z.date(), z.string()]).nullable().optional(),
  });

export type ListingDetailSchemaType = z.infer<typeof roomListingDetailSchema>;

export const houseListingDetailSchema = roomListingSchema
  .pick({
    bathrooms: true,
    bedrooms: true,
    petFriendly: true,
    contract: true,
    smokingAllowed: true,
    houseFeatures: true,
    latitude: true,
    longitude: true,
    locationNormalizedName: true,
    parishName: true,
    municipalityName: true,
    districtName: true,
  })
  .extend({
    /** Demand locations of interest, written as codes when saving. */
    locationCodes: z.array(z.string()).optional(),
  });

export type HouseListingDetailSchemaType = z.infer<
  typeof houseListingDetailSchema
>;

/**
 * Payload accepted by the listing update mutation/action. Covers the room +
 * house form fields plus plain realEstate columns (reference, propertyUrl) that
 * are edited directly, outside the form schema.
 */
export type UpdateRoomListingData = Partial<
  ListingDetailSchemaType & HouseListingDetailSchemaType
> & {
  reference?: string;
  propertyUrl?: string | null;
};

export const roomListingImagesUpdateSchema = z.object({
  imagesToAdd: z
    .array(z.instanceof(File))
    .refine(
      (files) => !files || files.every((file) => file.size <= MAX_IMAGE_SIZE),
      {
        message: `Cada imagem deve ter no máximo ${(MAX_IMAGE_SIZE / 1024 / 1024).toFixed(2)}MB`,
      },
    )
    .optional(),
  imagesToDelete: z.array(z.string().url()).optional(),
  orderOfImages: z.array(z.string()).optional(),
});
export type RoomListingImagesUpdateSchemaType = z.infer<
  typeof roomListingImagesUpdateSchema
>;

export const mobileRoomTitleDescriptionSchema = roomListingSchema.pick({
  title: true,
  description: true,
});

export type MobileRoomTitleDescriptionSchemaType = z.infer<
  typeof mobileRoomTitleDescriptionSchema
>;

export const mobileRoomPriceSchema = roomListingSchema.pick({
  rentPrice: true,
  deposit: true,
});

export type MobileRoomPriceSchemaType = z.infer<typeof mobileRoomPriceSchema>;

export const MAX_DOCUMENT_SIZE = 10 * 1024 * 1024; // 10MB
export const LISTING_STATUS_CHANGE_TYPE = "publishStatus";

export const listingHistoryPointSchema = z.object({
  changeType: z.string(),
  oldValue: z.string().nullable(),
  newValue: z.string(),
  changedAt: z.string(),
});
export type ListingHistoryPoint = z.infer<typeof listingHistoryPointSchema>;

export const listingHistoryResponseSchema = z.object({
  data: z.object({ history: z.array(listingHistoryPointSchema) }),
});
export type ListingHistoryResponse = z.infer<
  typeof listingHistoryResponseSchema
>;

export type ListingPriceChangeType = "salePrice" | "rentPrice";
export type ListingStatusHistoryPoint = {
  previousStatus: string | null;
  status: string;
  changedAt: string;
};
export type ListingPriceHistoryPoint = {
  previousPrice: number | null;
  price: number | null;
  changedAt: string;
};

// Price benchmark for a listing's zone (parish). Numeric fields are null when
// the sample is empty; `available` is false when there's no zone or no comps.
// The opposite side of the market in the same zone (room rent only): on a
// supply listing this is what seekers (demand) budget; on a demand listing it's
// what rooms (supply) ask. `listingType` is the opposite side.
const zonePriceOtherSchema = z.object({
  available: z.boolean(),
  listingType: z.string(),
  count: z.number(),
  avg: z.number().nullable().optional(),
  median: z.number().nullable().optional(),
  p25: z.number().nullable().optional(),
  p75: z.number().nullable().optional(),
  min: z.number().nullable().optional(),
  max: z.number().nullable().optional(),
});

// One zone's distribution. A supply listing has a single zone; a multi-zone
// demand has one per targeted zone (the FE shows a selector between them).
const zonePriceZoneSchema = z.object({
  available: z.boolean(),
  level: z.string().optional(),
  locationId: z.string().optional(),
  locationName: z.string().optional(),
  count: z.number().optional(),
  yourPrice: z.number().nullable().optional(),
  avg: z.number().nullable().optional(),
  median: z.number().nullable().optional(),
  p25: z.number().nullable().optional(),
  p75: z.number().nullable().optional(),
  min: z.number().nullable().optional(),
  max: z.number().nullable().optional(),
  position: z.enum(["below", "within", "above"]).nullable().optional(),
  other: zonePriceOtherSchema.nullable().optional(),
});

export const zonePriceStatsSchema = zonePriceZoneSchema.extend({
  reason: z.string().optional(),
  listingType: z.string().nullable().optional(),
  businessType: z.string().nullable().optional(),
  windowMonths: z.number().optional(),
  // True when `zones` are hierarchy tiers (parish → concelho → distrito) for a
  // supply listing, rather than a demand's chosen areas.
  tiered: z.boolean().optional(),
  // Per-zone breakdown — the top-level fields mirror the first (default) zone.
  zones: z.array(zonePriceZoneSchema).optional(),
});
export type ZonePriceStats = z.infer<typeof zonePriceStatsSchema>;
export type ZonePriceZone = z.infer<typeof zonePriceZoneSchema>;

// Raw price samples per zone for the FE histogram (lazy, separate endpoint).
// `values` is your side; `other.values` is the opposite side (room rent only).
const zonePriceDistributionZoneSchema = z.object({
  locationId: z.string().optional(),
  locationName: z.string().optional(),
  values: z.array(z.number()),
  other: z.object({
    listingType: z.string(),
    values: z.array(z.number()),
  }),
});

export const zonePriceDistributionSchema = z.object({
  available: z.boolean(),
  reason: z.string().optional(),
  listingType: z.string().nullable().optional(),
  businessType: z.string().nullable().optional(),
  windowMonths: z.number().optional(),
  tiered: z.boolean().optional(),
  yourPrice: z.number().nullable().optional(),
  zones: z.array(zonePriceDistributionZoneSchema),
});
export type ZonePriceDistribution = z.infer<typeof zonePriceDistributionSchema>;
export type ZonePriceDistributionZone = z.infer<
  typeof zonePriceDistributionZoneSchema
>;
